import { useState } from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import FilePDFIcon from '@/assets/svg/FilePDF.svg';
import FileJPEGIcon from '@/assets/svg/FileJPEG.svg';
import FileJPGIcon from '@/assets/svg/FileJPG.svg';
import FilePNGIcon from '@/assets/svg/FilePNG.svg';
import FileDOCXIcon from '@/assets/svg/FileDOCX.svg';
import { RAISE_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';
import type { ServiceType } from '../../types/serviceTypeTypes';
import type { FacilityListItem } from '@/epics/facilityManagement/types/facilityListType';
import type { EquipmentListItem } from '@/epics/facilityManagement/api/listEquipmentApiService';
import type { UserProfile } from '@/epics/userProfile/api/viewProfileApiService';

interface Step3Props {
  serviceType: ServiceType | null;
  clientInfo: UserProfile | null;
  clientInfoLoading: boolean;
  selectedFacility: FacilityListItem | null;
  selectedEquipment: EquipmentListItem | null;
  description: string;
  images: File[];
  document: File | null;
}

const PreviewSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="border border-[#DCDDE0] rounded-xl p-5 flex flex-col gap-4">
    <p className="text-[18px] font-medium text-on-surface-variant">{title}</p>
    {children}
  </div>
);

const LabelValue: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-1 min-w-0">
    <p className="text-xs text-[#474A4C]">{label}</p>
    <p className="text-base font-normal text-[#181A1B] min-w-0 wrap-break-word">
      {value || L.LABEL_EMPTY}
    </p>
  </div>
);

const formatFileSize = (bytes: number) => {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

const getFileIcon = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpeg':
      return <FileJPEGIcon className="w-8 h-8 shrink-0" />;
    case 'jpg':
      return <FileJPGIcon className="w-8 h-8 shrink-0" />;
    case 'png':
      return <FilePNGIcon className="w-8 h-8 shrink-0" />;
    case 'docx':
      return <FileDOCXIcon className="w-8 h-8 shrink-0" />;
    default:
      return <FilePDFIcon className="w-8 h-8 shrink-0" />;
  }
};

const FileRow: React.FC<{ file: File }> = ({ file }) => (
  <div className="flex items-center gap-3">
    {getFileIcon(file.name)}
    <div>
      <p className="text-base font-normal text-[#181A1B]">{file.name}</p>
      <p className="text-xs text-outline">{formatFileSize(file.size)}</p>
    </div>
  </div>
);

const Step3Preview: React.FC<Step3Props> = ({
  serviceType,
  clientInfo,
  clientInfoLoading,
  selectedFacility,
  selectedEquipment,
  description,
  images,
  document,
}) => {
  const [equipmentImageError, setEquipmentImageError] = useState(false);

  return (
    <div className="flex flex-col gap-6 mt-3">
      <div>
        <p className="text-[18px] font-normal text-on-surface-variant">
          {L.STEP3_SECTION_TITLE}
        </p>
        <p
          className="text-on-surface-variant mt-1"
          style={{
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.25px',
            fontWeight: 400,
          }}
        >
          {L.STEP3_SECTION_SUBTITLE}
        </p>
      </div>

      {/* Selected service */}
      <PreviewSection title={L.SECTION_SERVICE}>
        <div className="flex items-start gap-3">
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F3F4F6] shrink-0 mt-0.5">
            <WrenchScrewdriverIcon className="w-5 h-5 text-[#474A4C]" />
          </span>
          <div className="flex-1">
            <p className="font-normal text-[#181A1B] text-sm">
              {serviceType?.service_name ?? '-'}
            </p>
            <p className="text-sm text-[#474A4C]">
              {L.SERVICE_PRICE_PLACEHOLDER}
            </p>
          </div>
        </div>
        {serviceType?.description && (
          <p className="text-base text-[#474A4C]">{serviceType.description}</p>
        )}
      </PreviewSection>

      {/* Client data */}
      <PreviewSection title={L.SECTION_USER_DATA}>
        {clientInfoLoading ? (
          <div className="flex gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-1 flex flex-col gap-1">
                <div className="h-3 w-16 bg-[#F3F4F6] rounded animate-pulse" />
                <div className="h-4 w-28 bg-[#F3F4F6] rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <LabelValue
              label={L.LABEL_CLIENT_NAME}
              value={clientInfo?.name ?? null}
            />
            <LabelValue
              label={L.LABEL_CLIENT_ID}
              value={clientInfo?.clientId ?? null}
            />
            <LabelValue label={L.LABEL_ROLE} value={L.LABEL_ROLE_VALUE} />
          </div>
        )}
      </PreviewSection>

      {/* Facility data */}
      <PreviewSection title={L.SECTION_FACILITY_DATA}>
        <div className="grid grid-cols-3 gap-4">
          <LabelValue
            label={L.LABEL_FACILITY_NAME}
            value={selectedFacility?.name ?? null}
          />
          <LabelValue
            label={L.LABEL_FACILITY_ID}
            value={selectedFacility?.facilityId ?? null}
          />
        </div>
      </PreviewSection>

      {/* Address data */}
      <PreviewSection title={L.SECTION_ADDRESS_DATA}>
        <div className="grid grid-cols-3 gap-4">
          <LabelValue
            label={L.LABEL_ADDRESS1}
            value={selectedFacility?.addressLine1 ?? null}
          />
          <LabelValue
            label={L.LABEL_ADDRESS2}
            value={selectedFacility?.addressLine2 ?? null}
          />
          <LabelValue
            label={L.LABEL_CITY}
            value={selectedFacility?.city ?? null}
          />
          <LabelValue
            label={L.LABEL_STATE}
            value={selectedFacility?.state ?? null}
          />
          <LabelValue
            label={L.LABEL_ZIP}
            value={selectedFacility?.zipCode ?? null}
          />
          <LabelValue
            label={L.LABEL_COUNTRY}
            value={selectedFacility?.country ?? null}
          />
        </div>
      </PreviewSection>

      {/* Equipment data */}
      {selectedEquipment && (
        <PreviewSection title={L.SECTION_EQUIPMENT_DATA}>
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="bg-[#F2F4F5] border-b rounded-l-[4px] border-[#DCDDE0]">
                <th className="text-left text-sm text-[#474A4C] font-medium py-2 px-3">
                  {L.LABEL_EQUIP_TABLE_NAME}
                </th>
                <th className="text-left text-sm text-[#474A4C] font-medium py-2 px-3">
                  {L.LABEL_EQUIP_TABLE_ID}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-3 px-3 overflow-hidden">
                  <div className="flex items-center gap-3 min-w-0">
                    {selectedEquipment.image?.url && !equipmentImageError ? (
                      <img
                        src={selectedEquipment.image.url}
                        alt={selectedEquipment.name}
                        className="w-8 h-8 rounded-full object-cover shrink-0"
                        onError={() => setEquipmentImageError(true)}
                      />
                    ) : (
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F3F4F6] shrink-0">
                        <WrenchScrewdriverIcon className="w-4 h-4 text-[#474A4C]" />
                      </span>
                    )}
                    <span className="text-sm font-normal text-[#181A1B] min-w-0 wrap-break-word">
                      {selectedEquipment.name}
                    </span>
                  </div>
                </td>
                <td className="py-3 px-3 text-sm text-on-surface-variant">
                  {selectedEquipment.equipmentId}
                </td>
              </tr>
            </tbody>
          </table>
        </PreviewSection>
      )}

      {/* Details and attachments */}
      <PreviewSection title={L.SECTION_DETAILS}>
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-xs text-[#474A4C] mb-1">{L.LABEL_DESCRIPTION}</p>
            <p className="text-base font-normal text-[#181A1B] whitespace-pre-wrap break-all">
              {description || L.LABEL_EMPTY}
            </p>
          </div>

          {images.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-[#474A4C]">{L.LABEL_IMAGES}</p>
              {images.map((img) => (
                <FileRow key={img.name} file={img} />
              ))}
            </div>
          )}

          {document && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-[#474A4C]">{L.LABEL_DOCUMENT}</p>
              <FileRow file={document} />
            </div>
          )}
        </div>
      </PreviewSection>
    </div>
  );
};

export default Step3Preview;
